import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Observable } from 'rxjs';
import { IPhModalAnswer } from '../../core/modals/ph-modal/models/phModalAnswer';
import { IPhModalData } from '../../core/modals/ph-modal/models/phModalData';
import { PhModalComponent } from '../../core/modals/ph-modal/ph-modal.component';
import { DwUserActivityNames } from '../../core/models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../core/models/dwUsersActivityTypes';
import { IGroupedBarChartData } from '../../core/models/groupedBarChart';
import { PHPermissions } from '../../core/models/PHPermissions';
import { ComplianceCheckBlockReason } from '../../core/models/photoReport';
import { MixpanelEvents } from '../../core/models/mixpanelEvents';
import { DwUsersActivityService } from '../../core/services/dw-users-activity.service';
import { ImageRecognitionService } from '../../core/services/image-recognition.service';
import { MixpanelService } from '../../core/services/mixpanel.service';
import { PlanogramsService } from '../../core/services/planograms.service';
import { SessionService } from '../../core/services/session.service';
import { SnackBarService } from '../../core/services/snack-bar.service';
import { ExcelService } from '../../core/services/xls.service';
import {
	ControlCancelSendModalComponent,
	IControlCancelSendModalData,
} from '../modals/control-cancel-send-modal/control-cancel-send-modal.component';
import {
	IControlChildIdPair,
	IControlChildrenResponse,
	IControlPaginatedChildrenResponse,
	IControlPlanogramItem,
} from '../models/control-children.model';
import {
	IControlBulkActionContext,
	IControlBulkCancelRequest,
	IControlBulkCancelOptions,
	IControlBulkConfirmOptions,
	IControlBulkConfirmRequest,
	IControlBulkRecognizeOptions,
	IControlBulkRecognizeRequest,
	IControlBulkReturnOptions,
	IControlBulkReturnRequest,
	IControlBulkSendOptions,
	IControlBulkSendRequest,
	IControlExecutionPayload,
} from '../models/control-execution.model';
import {
	IControlDashboardTableData,
	IControlOverviewItem,
	IControlTaskCounts,
} from '../models/control-dashboard-table.model';

export type ControlExportType = 'table' | 'photoreports' | 'full';

const TABLE_URL: string = '/api/v1/control-table/';
const CHILDREN_URL: string = `${TABLE_URL}children/`;
const CHILDREN_IDS_URL: string = `${TABLE_URL}children-ids/`;
const CHART_URL: string = '/api/v1/control-chart/';
const OVERVIEW_URL: string = '/api/v1/control-overview/';
const TASK_COUNTS_URL: string = '/api/v1/users/control-task-counts/';

@Injectable({ providedIn: 'root' })
export class ControlService {

	dashboardLoadParams: string = null;
	dashboardFiltersApplied: boolean = false;

	private readonly isSendingState: WritableSignal<boolean> = signal(false);
	private readonly isCancellingState: WritableSignal<boolean> = signal(false);
	private readonly isReturningState: WritableSignal<boolean> = signal(false);
	private readonly isConfirmingState: WritableSignal<boolean> = signal(false);
	private readonly isRecognizingState: WritableSignal<boolean> = signal(false);
	readonly isSending: Signal<boolean> = this.isSendingState.asReadonly();
	readonly isCancelling: Signal<boolean> = this.isCancellingState.asReadonly();
	readonly isReturning: Signal<boolean> = this.isReturningState.asReadonly();
	readonly isConfirming: Signal<boolean> = this.isConfirmingState.asReadonly();
	readonly isRecognizing: Signal<boolean> = this.isRecognizingState.asReadonly();

	private readonly http: HttpClient = inject(HttpClient);
	private readonly excelService: ExcelService = inject(ExcelService);
	private readonly dialog: MatDialog = inject(MatDialog);
	private readonly planogramsService: PlanogramsService = inject(PlanogramsService);
	private readonly imageRecognitionService: ImageRecognitionService = inject(ImageRecognitionService);
	private readonly snackBarService: SnackBarService = inject(SnackBarService);
	private readonly translateService: TranslateService = inject(TranslateService);
	private readonly dwUsersActivityService: DwUsersActivityService = inject(DwUsersActivityService);
	private readonly sessionService: SessionService = inject(SessionService);
	private readonly mixPanelService: MixpanelService = inject(MixpanelService);

	getDashboardChart(params: any = {}): Observable<IGroupedBarChartData> {
		return this.http.get<IGroupedBarChartData>(CHART_URL, {
			params: new HttpParams({ fromObject: params }),
		});
	}

	getDashboardCards(params: any = {}): Observable<Array<IControlOverviewItem>> {
		return this.http.get<Array<IControlOverviewItem>>(OVERVIEW_URL, {
			params: new HttpParams({ fromObject: params }),
		});
	}

	getControlTaskCounts(): Observable<IControlTaskCounts> {
		return this.http.get<IControlTaskCounts>(TASK_COUNTS_URL);
	}

	getDashboardTable(params: any = {}): Observable<IControlDashboardTableData> {
		return this.http.get<IControlDashboardTableData>(TABLE_URL, {
			params: new HttpParams({ fromObject: params }),
		});
	}

	getTableChildren(params: any = {}, status?: string): Observable<IControlPaginatedChildrenResponse> {
		const query = status != null ? { ...params, status } : params;
		return this.http.get<IControlPaginatedChildrenResponse>(CHILDREN_URL, {
			params: new HttpParams({ fromObject: query }),
		});
	}

	getTableChildrenIds(params: any = {}, status?: string): Observable<Array<IControlChildIdPair>> {
		const query = status != null ? { ...params, status } : params;
		return this.http.get<Array<IControlChildIdPair>>(CHILDREN_IDS_URL, {
			params: new HttpParams({ fromObject: query }),
		});
	}

	getPhotoReports(planogramId: number, planId: number): Observable<any> {
		return this.http.get<any>(`/api/layout/stands/${planogramId}/photoreports/?planogram_id=${planId}`);
	}

	getEntityChildren(entityId: number, params: any = {}): Observable<IControlChildrenResponse> {
		return this.http.get<IControlChildrenResponse>(`${TABLE_URL}${entityId}/children/`, {
			params: new HttpParams({ fromObject: params }),
		});
	}

	bulkCancelExecutions(executionIds: Array<number>): Observable<void> {
		return this.http.post<void>('/api/v1/executions/bulk-cancel/', { execution_ids: executionIds });
	}

	exportTable(params: any = {}): Observable<void> {
		return this.http.post<void>(`${TABLE_URL}export/`, null, {
			params: new HttpParams({ fromObject: params }),
		});
	}

	downloadReport(name: string, params: any = {}): Observable<void> {
		return this.excelService.exportProductHoles(
			{ ...params, sheet_name: [name] },
			name
		);
	}

	confirmAndSend(
		context: IControlBulkActionContext,
		request: IControlBulkSendRequest,
		options: IControlBulkSendOptions = {},
	): void {
		if (this.isSendingState() || !request.items.length || !request.execution_data.length) return;

		const count = request.items.length;
		this.dialog
			.open<PhModalComponent, IPhModalData, IPhModalAnswer>(PhModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					icon: 'send',
					title: this.translateService.instant('CONTROL_DASHBOARD.SEND_TO_EXECUTION_TITLE'),
					description: [
						this.translateService.instant('CONTROL_DASHBOARD.SEND_TO_EXECUTION_CONFIRM', { count }),
						this.translateService.instant('CONTROL_DASHBOARD.SEND_TO_EXECUTION_WARNING'),
					].join('\n\n'),
					buttons: [
						{
							id: 'cancel',
							label: 'COMMON.CANCEL',
							type: 'secondary',
						},
						{
							id: 'confirm',
							label: this.translateService.instant('CONTROL_DASHBOARD.SEND_TO_EXECUTION_BUTTON', { count }),
							type: 'primary',
						},
					],
				},
			})
			.afterClosed()
			.subscribe((result: IPhModalAnswer) => {
				if (result?.buttonId !== 'confirm') return;
				this.send(context, request, options);
			});
	}

	confirmAndCancel(
		context: IControlBulkActionContext,
		request: IControlBulkCancelRequest,
		options: IControlBulkCancelOptions = {},
	): void {
		if (this.isCancellingState() || !request.items.length || !request.execution_ids.length) return;

		const count = request.items.length;
		const currentUserId = this.sessionService.defaults.user_id;
		const foreignCount = request.items.filter((item: IControlPlanogramItem) => {
			const senderId = item.data.execution?.user?.id;
			return senderId != null && senderId !== currentUserId;
		}).length;
		const scheduledOnly = request.items.every((item: IControlPlanogramItem) => !!item.data.execution?.scheduled_date);
		const unscheduledOnly = request.items.every((item: IControlPlanogramItem) => !item.data.execution?.scheduled_date);
		const uniquePlansCount = new Set(request.items.map((item: IControlPlanogramItem) => item.data.plan.id)).size;

		this.dialog
			.open<ControlCancelSendModalComponent, IControlCancelSendModalData, IPhModalAnswer>(
				ControlCancelSendModalComponent,
				{
					width: '674px',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: { count, foreignCount, uniquePlansCount, scheduledOnly, unscheduledOnly },
				}
			)
			.afterClosed()
			.subscribe((result: IPhModalAnswer) => {
				if (result?.buttonId !== 'confirm') return;
				this.cancel(context, request, options);
			});
	}

	confirmAndReturn(
		context: IControlBulkActionContext,
		request: IControlBulkReturnRequest,
		options: IControlBulkReturnOptions = {},
	): void {
		if (this.isReturningState() || !request.items.length) return;

		const count = request.items.length;
		this.dialog
			.open<PhModalComponent, IPhModalData, IPhModalAnswer>(PhModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					icon: 'returnExecution',
					title: this.translateService.instant('CONTROL_DASHBOARD.RETURN_TO_EXECUTION_TITLE'),
					description: [
						this.translateService.instant('CONTROL_DASHBOARD.RETURN_TO_EXECUTION_CONFIRM'),
						this.translateService.instant('CONTROL_DASHBOARD.RETURN_TO_EXECUTION_WARNING'),
						this.translateService.instant('CONTROL_DASHBOARD.RETURN_TO_EXECUTION_DATE_HINT'),
					].join('\n\n'),
					buttons: [
						{
							id: 'cancel',
							label: 'COMMON.CANCEL',
							type: 'secondary',
						},
						{
							id: 'confirm',
							label: this.translateService.instant('CONTROL_DASHBOARD.RETURN_TO_EXECUTION_BUTTON', { count }),
							type: 'primary',
						},
					],
				},
			})
			.afterClosed()
			.subscribe((result: IPhModalAnswer) => {
				if (result?.buttonId !== 'confirm') return;
				this.returnForExecution(context, request, options);
			});
	}

	confirmAndCheck(
		context: IControlBulkActionContext,
		request: IControlBulkConfirmRequest,
		options: IControlBulkConfirmOptions = {},
	): void {
		if (this.isConfirmingState() || !request.items.length) return;

		const count = request.items.length;
		this.dialog
			.open<PhModalComponent, IPhModalData, IPhModalAnswer>(PhModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					icon: 'verified',
					title: this.translateService.instant('CONTROL_DASHBOARD.CONFIRM_EXECUTION_TITLE'),
					description: this.translateService.instant('CONTROL_DASHBOARD.CONFIRM_EXECUTION_CONFIRM', { count }),
					buttons: [
						{
							id: 'cancel',
							label: 'COMMON.CANCEL',
							type: 'secondary',
						},
						{
							id: 'confirm',
							label: this.translateService.instant('CONTROL_DASHBOARD.CONFIRM_EXECUTION_BUTTON', { count }),
							type: 'primary',
						},
					],
				},
			})
			.afterClosed()
			.subscribe((result: IPhModalAnswer) => {
				if (result?.buttonId !== 'confirm') return;
				this.confirmExecution(context, request, options);
			});
	}

	confirmAndRecognize(
		context: IControlBulkActionContext,
		request: IControlBulkRecognizeRequest,
		options: IControlBulkRecognizeOptions = {},
	): void {
		if (this.isRecognizingState()) return;
		if (
			!this.sessionService.defaults.has_image_recognition_access
			|| !this.sessionService.hasAccess(PHPermissions.PhotoreportRecognition)
		) {
			return;
		}

		const items = request.items.filter(
			(item) =>
				!!item.data.compliance?.can_run_compliance_check
				&& item.data.compliance?.photoreport_id != null,
		);
		if (!items.length) return;

		const count = items.length;
		const promoModal = !!options.promoModal;
		this.dialog
			.open<PhModalComponent, IPhModalData, IPhModalAnswer>(PhModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					icon: 'imageRecognition',
					title: this.translateService.instant('CONTROL_DASHBOARD.RECOGNIZE_PHOTO_REPORTS_TITLE'),
					description: this.translateService.instant(
						promoModal
							? 'CONTROL_DASHBOARD.RECOGNIZE_PHOTO_REPORT_PROMO'
							: 'CONTROL_DASHBOARD.RECOGNIZE_PHOTO_REPORTS_CONFIRM',
						promoModal ? {} : { count },
					),
					buttons: [
						{
							id: 'cancel',
							label: 'COMMON.CANCEL',
							type: 'secondary',
						},
						{
							id: 'confirm',
							label: this.translateService.instant(
								promoModal
									? 'CONTROL_DASHBOARD.RECOGNIZE_PHOTO_REPORT_BUTTON'
									: 'CONTROL_DASHBOARD.RECOGNIZE_PHOTO_REPORTS_BUTTON',
								promoModal ? {} : { count },
							),
							type: 'primary',
						},
					],
				},
			})
			.afterClosed()
			.subscribe((result: IPhModalAnswer) => {
				if (result?.buttonId !== 'confirm') return;
				this.recognize(context, { items }, options);
			});
	}

	private updateSelectionAfterSuccess(
		context: IControlBulkActionContext,
		items: Array<IControlPlanogramItem>,
		clearAllSelection: boolean = true,
	): void {
		if (clearAllSelection) {
			context.cardService.clearSelected();
		} else {
			context.cardService.deselectItems(items);
		}
	}

	trackDashboardNavigateToStatus(source: string, status?: string): void {
		this.mixPanelService.trackPage(MixpanelEvents.ControlDashboardNavigateToStatus, {
			source,
			...(status ? { status: status.toLowerCase() } : {}),
		});
	}

	private resolveSendEvent(executionData: Array<IControlExecutionPayload>): MixpanelEvents {
		const hasScheduled = executionData.some((item) => !!item.scheduled_date);
		const hasImmediate = executionData.some((item) => !item.scheduled_date);
		if (hasScheduled && hasImmediate) return MixpanelEvents.ControlSendAndSchedule;
		if (hasScheduled) return MixpanelEvents.ControlSchedule;
		return MixpanelEvents.ControlSend;
	}

	private trackStatusAction(event: MixpanelEvents, items: Array<IControlPlanogramItem>): void {
		const status = items[0]?.status;
		if (!status) return;
		this.mixPanelService.trackPage(event, {
			status: String(status).toLowerCase(),
			count: items.length,
			is_mass: items.length > 1,
		});
	}

	private send(
		context: IControlBulkActionContext,
		request: IControlBulkSendRequest,
		options: IControlBulkSendOptions,
	): void {
		if (this.isSendingState()) return;

		this.isSendingState.set(true);
		this.planogramsService
			.sendBulkExecutions({ execution_data: request.execution_data })
			.pipe(finalize(() => this.isSendingState.set(false)))
			.subscribe({
				next: () => {
					if (options.removeItemsOnSuccess) {
						context.removeItems(request.items);
					}
					this.updateSelectionAfterSuccess(context, request.items, options.clearAllSelection ?? true);
					this.trackStatusAction(this.resolveSendEvent(request.execution_data), request.items);
				},
				error: () => this.snackBarService.setErrorNotification(),
			});
	}

	private cancel(
		context: IControlBulkActionContext,
		request: IControlBulkCancelRequest,
		options: IControlBulkCancelOptions,
	): void {
		if (this.isCancellingState()) return;

		this.isCancellingState.set(true);
		this.dwUsersActivityService.track(DwUserActivityNames.Control, DwUserActivityTypes.CancelExecution);
		this.bulkCancelExecutions(request.execution_ids)
			.pipe(finalize(() => this.isCancellingState.set(false)))
			.subscribe({
				next: () => {
					context.removeItems(request.items);
					this.updateSelectionAfterSuccess(context, request.items, options.clearAllSelection ?? true);
					this.trackStatusAction(MixpanelEvents.ControlCancel, request.items);
				},
				error: () => this.snackBarService.setErrorNotification(),
			});
	}

	private returnForExecution(
		context: IControlBulkActionContext,
		request: IControlBulkReturnRequest,
		options: IControlBulkReturnOptions,
	): void {
		if (this.isReturningState()) return;

		this.isReturningState.set(true);
		Promise.all(
			request.items.map((item) =>
				this.planogramsService.returnForExecution(
					item.data.planogram.id,
					context.cardService.buildReturnForExecutionData(item),
				)
			)
		)
			.then(() => {
				if (options.removeItemsOnSuccess) {
					context.removeItems(request.items);
				}
				this.updateSelectionAfterSuccess(context, request.items, options.clearAllSelection ?? true);
				this.trackStatusAction(MixpanelEvents.ControlReturn, request.items);
			})
			.catch(() => this.snackBarService.setErrorNotification())
			.finally(() => this.isReturningState.set(false));
	}

	private confirmExecution(
		context: IControlBulkActionContext,
		request: IControlBulkConfirmRequest,
		options: IControlBulkConfirmOptions,
	): void {
		if (this.isConfirmingState()) return;

		this.isConfirmingState.set(true);
		Promise.all(
			request.items.map((item) =>
				this.planogramsService.checkPhotoReports(
					[item.data.planogram.id],
					context.cardService.buildConfirmExecutionParams(item),
				)
			)
		)
			.then(() => {
				if (options.removeItemsOnSuccess) {
					context.removeItems(request.items);
				}
				this.updateSelectionAfterSuccess(context, request.items, options.clearAllSelection ?? true);
				this.trackStatusAction(MixpanelEvents.ControlConfirm, request.items);
			})
			.catch(() => this.snackBarService.setErrorNotification())
			.finally(() => this.isConfirmingState.set(false));
	}

	private recognize(
		context: IControlBulkActionContext,
		request: IControlBulkRecognizeRequest,
		options: IControlBulkRecognizeOptions,
	): void {
		if (this.isRecognizingState()) return;

		const payload = request.items
			.map((item) => ({
				planogram_id: item.data.planogram.id,
				photoreport_id: item.data.compliance?.photoreport_id,
			}))
			.filter((item): item is { planogram_id: number; photoreport_id: number } => item.photoreport_id != null);
		if (!payload.length) return;

		const userId = this.sessionService.defaults.user_id;
		context.applyComplianceUpdates(
			request.items.map((item) => ({
				planogram_id: item.data.planogram.id,
				compliance: {
					photoreport_id: item.data.compliance?.photoreport_id ?? null,
					can_run_compliance_check: false,
					compliance_check_block_reason: ComplianceCheckBlockReason.AlreadyRunning,
					compliance_check_user_id: userId,
					compliance_result: item.data.compliance?.compliance_result ?? null,
				},
			})),
		);

		this.isRecognizingState.set(true);
		this.imageRecognitionService
			.startComplianceCheck(payload)
			.pipe(finalize(() => this.isRecognizingState.set(false)))
			.subscribe({
				next: () => {
					this.updateSelectionAfterSuccess(context, request.items, options.clearAllSelection ?? true);
					this.trackStatusAction(MixpanelEvents.ControlRecognize, request.items);
				},
				error: () => {
					context.applyComplianceUpdates(
						request.items.map((item) => ({
							planogram_id: item.data.planogram.id,
							compliance: {
								photoreport_id: item.data.compliance?.photoreport_id ?? null,
								can_run_compliance_check: true,
								compliance_check_block_reason: null,
								compliance_check_user_id: null,
								compliance_result: item.data.compliance?.compliance_result ?? null,
							},
						})),
					);
					this.snackBarService.setErrorNotification();
				},
			});
	}
}
